<template>
  <f7-toolbar top class="top-menu">
    <div class="top-menu__inner">
      <!-- Menú de inicio -->
      <template v-if="isDashboard || inKeepHome">
        <f7-link
          v-for="(item, idx) in homeItems"
          :key="`home-item-${idx}`"
          class="top-menu__link"
          :class="{ 'is-active': isActiveHome(item) }"
          :data-tour-id="getMenuTourDataId(item)"
          :href="item.url || undefined"
          @click="onItemClick(item, $event)"
        >
          <span
            class="top-menu__text"
            :data-tour-id="getMenuTourDataId(item)"
          >
            {{ displayText(item) }}
          </span>
          <span v-if="item.badge" class="top-menu__badge">{{
            item.badge
          }}</span>
        </f7-link>

        <div class="top-menu__spacer"></div>

        <!-- “¿Podemos ayudarte?”: sin href para evitar navegación -->
        <f7-link
          v-if="homeHelp"
          class="top-menu__help"
          :href="undefined"
          @click.prevent.stop="onHelpClick"
        >
          {{ $t(homeHelp.text) }}
        </f7-link>
      </template>

      <!-- Menú simple (volver + ayuda) -->
      <template v-else-if="inSimple">
        <!-- Volver (izquierda) -->
        <f7-link
          v-if="simpleBack"
          class="top-menu__link top-menu__back"
          href="javascript:void(0)"
          @click="goBack"
          aria-label="Volver"
        >
          <svg
            class="top-menu__back-icon"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              d="M15 18 L9 12 L15 6"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span class="top-menu__text">{{
            displayText(simpleBack)
          }}</span>
        </f7-link>

        <div class="top-menu__spacer"></div>

        <!-- Ayuda (derecha): sin href para evitar navegación -->
        <f7-link
          v-if="simpleHelp"
          class="top-menu__help"
          :href="undefined"
          @click.prevent.stop="onHelpClick"
        >
          {{ $t(simpleHelp.text) }}
        </f7-link>
      </template>

      <!-- Vistas con pestañas centradas (muestras, documentos, alertas) -->
      <template v-else-if="activeTabs && activeTabs.length">
        <div class="top-menu__zone top-menu__zone--left">
          <!-- Volver (izquierda) -->
          <f7-link
            v-if="contextBack"
            class="top-menu__link top-menu__back"
            href="javascript:void(0)"
            @click="goBack"
            aria-label="Volver"
          >
            <svg
              class="top-menu__back-icon"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                d="M15 18 L9 12 L15 6"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span class="top-menu__text">{{
              displayText(contextBack)
            }}</span>
          </f7-link>
        </div>

        <div class="top-menu__zone top-menu__zone--center">
          <!-- Pestañas centradas -->
          <div class="top-menu__tabs">
            <f7-link
              v-for="(tab, i) in activeTabs"
              :key="`tab-${activeContext}-${i}`"
              class="top-menu__link"
              :class="{ 'is-active': isActiveTab(tab) }"
              :data-tour-id="getMenuTourDataId(tab)"
              :href="tab.url || undefined"
              @click="onItemClick(tab, $event)"
            >
              <span
                class="top-menu__text"
                :data-tour-id="getMenuTourDataId(tab)"
              >
                {{ displayText(tab) }}
              </span>
            </f7-link>
          </div>
        </div>

        <div class="top-menu__zone top-menu__zone--right">
          <!-- Ayuda (derecha): sin href para evitar navegación -->
          <f7-link
            v-if="contextHelp"
            class="top-menu__help"
            :href="undefined"
            @click.prevent.stop="onHelpClick"
          >
            {{ $t(contextHelp.text) }}
          </f7-link>
        </div>
      </template>

      <!-- Respaldo: muestra el menú de inicio -->
      <template v-else>
        <f7-link
          v-for="(item, idx) in homeItems"
          :key="`fallback-item-${idx}`"
          class="top-menu__link"
          :class="{ 'is-active': isActiveHome(item) }"
          :data-tour-id="getMenuTourDataId(item)"
          :href="item.url || undefined"
          @click="onItemClick(item, $event)"
        >
          <span
            class="top-menu__text"
            :data-tour-id="getMenuTourDataId(item)"
          >
            {{ displayText(item) }}
          </span>
        </f7-link>

        <div class="top-menu__spacer"></div>

        <!-- Ayuda: sin href para evitar navegación -->
        <f7-link
          v-if="homeHelp"
          class="top-menu__help"
          :href="undefined"
          @click.prevent.stop="onHelpClick"
        >
          {{ $t(homeHelp.text) }}
        </f7-link>
      </template>
    </div>
  </f7-toolbar>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { NOTIFY_TIME_LONG, IS_LOCAL_TEST } from '@/js/constants';
import { blockedRoutePatterns } from '@/js/routeAccess';

export default {
  name: 'Menu',
  props: {
    // La configuración del menú llega como un objeto { home: {...}, contexts: {...} }
    menuConfig: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    // Permite forzar la ruta activa desde fuera, útil para tests o estados especiales
    activePath: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      // Guardamos la ruta actual normalizada en un estado reactivo.
      // Esto se usa para marcar qué enlace está activo y qué versión de menú se muestra.
      curPath: '/',
      // Mensaje asociado a notificaciones de error.
      notificationMessage: '',
      lastBackRoute: null, // Ruta actual en la última navegación
      lastPrevRoute: null, // Ruta previa usada la última vez
      navStack: [],
      navMax: 30,
      ignoringNext: false,
    };
  },

  computed: {
    // La ruta actual se obtiene de props si está forzada, o del estado interno en caso contrario
    currentPath() {
      if (this.activePath) return this.normalizePath(this.activePath);
      return this.curPath || '/';
    },

    // Items y ayuda configurados en la parte de "home"
    homeItems() {
      const h = (this.menuConfig && this.menuConfig.home) || {};
      const items = h.items || [];

      // Reutiliza el mismo bloqueo centralizado que routeAccess.js
      // para no duplicar reglas entre menu y acceso por URL.
      return items.filter((item) => {
        return !blockedRoutePatterns.includes(item.url);
      });
    },
    homeHelp() {
      const h = (this.menuConfig && this.menuConfig.home) || {};
      return h.help || null;
    },

    // Contextos posibles (simple, samples, documents, alerts…)
    contexts() {
      return (this.menuConfig && this.menuConfig.contexts) || {};
    },

    // Detectores de en qué contexto estamos, comparando la ruta con las declaradas en el JSON
    isDashboard() {
      return this.currentPath === '/';
    },
    inKeepHome() {
      const list =
        (this.contexts.keepHomeActive &&
          this.contexts.keepHomeActive.routes) ||
        [];
      return this.isInRoutes(list);
    },
    inSimple() {
      const list =
        (this.contexts.simple && this.contexts.simple.routes) || [];
      return this.isInRoutes(list);
    },
    inSamples() {
      const list =
        (this.contexts.samples && this.contexts.samples.routes) || [];
      return this.isInRoutes(list);
    },
    inDocuments() {
      const list =
        (this.contexts.documents && this.contexts.documents.routes) ||
        [];
      return this.isInRoutes(list);
    },
    inAlerts() {
      const list =
        (this.contexts.alerts && this.contexts.alerts.routes) || [];
      return this.isInRoutes(list);
    },

    // Devuelve qué contexto está activo, lo usamos para resolver back/help/tabs
    activeContext() {
      if (this.inSamples) return 'samples';
      if (this.inDocuments) return 'documents';
      if (this.inAlerts) return 'alerts';
      return null;
    },
    contextObj() {
      return this.activeContext
        ? this.contexts[this.activeContext] || null
        : null;
    },
    contextBack() {
      return this.contextObj && this.contextObj.back
        ? this.contextObj.back
        : null;
    },
    contextHelp() {
      return this.contextObj && this.contextObj.help
        ? this.contextObj.help
        : null;
    },
    activeTabs() {
      return this.contextObj && this.contextObj.tabs
        ? this.contextObj.tabs
        : [];
    },

    // Propiedades para el layout “simple”
    simpleBack() {
      return (
        (this.contexts.simple && this.contexts.simple.back) || null
      );
    },
    simpleHelp() {
      return (
        (this.contexts.simple && this.contexts.simple.help) || null
      );
    },

    ...mapGetters('user', ['getLanguage']),
  },

  mounted() {
    this.$f7ready(() => {
      const handleRouteChange = (to) => {
        const router = this.$f7?.views?.main?.router;

        // OJO: mejor url para stack, path para pintar activo
        const url = router?.currentRoute?.url || '/';
        const path = this.normalizePath(
          router?.currentRoute?.path || '/'
        );

        if (to?.route?.modal || to?.modal) return;
        if (document.querySelector('.calendar-modal, .picker-modal'))
          return;

        // Si venimos de un back controlado: NO apilar, PERO sí actualizar UI
        if (this.ignoringNext) {
          this.ignoringNext = false;
          this.curPath = path; // esto arregla el activo del menú
          return;
        }

        this.pushToStack(url); // guarda URL real (con / final si aplica)
        this.curPath = path; // para isActiveHome/isActiveTab
      };

      this.$f7.on('routeChange', handleRouteChange);
      this.$f7?.views?.main?.router?.on?.(
        'routeChange',
        handleRouteChange
      );

      // Inicializa con la actual
      const router = this.$f7?.views?.main?.router;
      this.pushToStack(router?.currentRoute?.url || '/'); // URL real para stack
      this.curPath = this.normalizePath(
        router?.currentRoute?.path || '/'
      ); // path para UI (activo)
    });
  },

  beforeDestroy() {
    // Limpieza de eventos para evitar fugas de memoria
    try {
      this.$f7?.off?.('routeChange');
      const view = this.$f7?.views?.main;
      view?.router?.off?.('routeChange');
    } catch (e) {
      this.notifyError(
        e,
        'No se pudieron desuscribir los eventos del router'
      );
    }
    window.removeEventListener('popstate', this.syncFromRouter);
    window.removeEventListener('hashchange', this.syncFromRouter);
  },

  methods: {
    getMenuTourDataId(item) {
      if (
        this.normalizePath(item?.url) ===
        this.normalizePath('/SamplesList/')
      ) {
        return 'samples-register-top-menu-option';
      }
      if (
        this.normalizePath(item?.url) ===
        this.normalizePath('/issued-invoices-list/')
      ) {
        return 'issued-invoices-top-menu-option';
      }
      return null;
    },
    pushToStack(url) {
      if (!url) return;
      const s = String(url);
      if (s.startsWith('javascript:')) return;

      const stack = this.navStack;
      const last = stack[stack.length - 1];

      if (last === url) return;
      stack.push(url);

      if (stack.length > this.navMax) {
        stack.splice(0, stack.length - this.navMax);
      }
    },

    goBackSmart() {
      const router = this.$f7?.views?.main?.router || this.$f7router;
      if (!router) return;

      // 1) Si no hay suficiente historial propio => home
      if (
        !Array.isArray(this.navStack) ||
        this.navStack.length <= 1
      ) {
        router.navigate('/', {
          animate: true,
          transition: 'f7-fade',
        });
        return;
      }

      // Helper: clave para comparar sin cargarte el URL real
      const keyOf = (url) => {
        if (!url) return '/';
        // Quitamos query/hash para comparar "pantallas"
        const base = String(url).split('?')[0].split('#')[0];
        // Normalización suave: solo asegura leading slash. NO quites trailing slash aquí si tu router lo necesita.
        return base.startsWith('/') ? base : `/${base}`;
      };

      // Estado actual (URL real)
      const curUrl = this.navStack[this.navStack.length - 1];
      const curKey = keyOf(curUrl);

      // Quitamos la actual
      this.navStack.pop();

      // 2) Queremos elegir un target que NO sea ping-pong.
      // Detectamos alternancia mirando los últimos elementos del stack.
      // Permitimos hasta 3 saltos.
      let targetUrl = this.navStack[this.navStack.length - 1] || '/';
      let tries = 0;

      while (tries < 3 && this.navStack.length) {
        const targetKey = keyOf(targetUrl);

        // Caso 1: si por lo que sea coincide con la actual (raro, pero por seguridad)
        if (targetKey === curKey) {
          this.navStack.pop();
          targetUrl = this.navStack[this.navStack.length - 1] || '/';
          tries++;
          continue;
        }

        // Caso 2: ping-pong clásico A,B,A,B:
        // si el siguiente target y el anterior del stack alternan con la actual,
        // saltamos uno más atrás.
        const prevUrl = this.navStack[this.navStack.length - 2]; // el que habría antes del target
        const prevKey = keyOf(prevUrl);
        if (prevUrl && prevKey === curKey) {
          // ...A, B, A  (cur=A, target=B, prev=A) => saltar B
          this.navStack.pop(); // quita B
          targetUrl = this.navStack[this.navStack.length - 1] || '/';
          tries++;
          continue;
        }

        break; // target válido
      }

      if (!targetUrl) targetUrl = '/';

      // 3) Navegar sin re-apilar en tu listener
      this.ignoringNext = true;
      this.curPath = this.normalizePath(
        String(targetUrl).split('?')[0].split('#')[0]
      ); // UI
      router.navigate(targetUrl, {
        animate: true,
        transition: 'f7-fade',
        replaceState: true,
      });
    },
    safeGetCurrentPath() {
      try {
        const raw =
          this.$f7?.views?.main?.router?.currentRoute?.path || '/';
        return this.normalizePath(raw);
      } catch (e) {
        this.notifyError(e, 'No se pudo obtener la ruta actual');
        return '/';
      }
    },

    syncFromRouter() {
      this.curPath = this.safeGetCurrentPath();
    },

    // Normaliza para comparar rutas de UI sin que afecte si vienen
    // con o sin barra final.
    normalizePath(p) {
      if (!p) return '/';
      let s = String(p).trim().split(/[?#]/)[0];
      if (!s.startsWith('/')) s = `/${s}`;
      if (s.length > 1 && s.endsWith('/')) {
        s = s.slice(0, -1);
      }
      return s;
    },

    // Comprueba si la ruta actual empieza por alguna del listado
    isInRoutes(routeList) {
      const cur = this.currentPath;
      for (const r of routeList) {
        const norm = this.normalizePath(r);
        if (norm && cur.startsWith(norm)) {
          return true;
        }
      }
      return false;
    },

    // Devuelve el texto traducido o el literal si no hay traducción
    displayText(item) {
      const key = (item && (item.name || item.title)) || '';
      const translated = this.$t ? this.$t(key) : key;
      return translated || key;
    },

    // Marca activo un item de home si coincide exacto o es prefijo (salvo raíz)
    isActiveHome(item) {
      if (!item || !item.url) return false;
      const cur = this.currentPath;
      const target = this.normalizePath(item.url);
      return (
        cur === target || (target !== '/' && cur.startsWith(target))
      );
    },

    // Marca activo un tab si coincide exacto o es prefijo
    isActiveTab(tab) {
      if (!tab || !tab.url) return false;

      const cur = this.currentPath;
      const target = this.normalizePath(tab.url);

      // Si la ruta actual empieza por el mismo prefijo base del tab
      // (por ejemplo, /agronomy/, /food/, /environment/, etc.)
      const tabBase = target.split('/')[1]; // Ej: "agronomy"
      if (tabBase && cur.startsWith(`/${tabBase}/`)) {
        // Excluir el caso de alerts, que se gestiona más abajo
        if (tabBase !== 'alerts') return true;
      }

      // Mantener los casos especiales de alerts
      if (target.startsWith('/alerts/')) {
        const subSection = target.split('/')[2]; // params o sample
        if (subSection && cur.startsWith(`/alerts/${subSection}`))
          return true;
        return false;
      }

      // Coincidencia directa o prefijo exacto
      return cur === target || cur.startsWith(target);
    },

    onItemClick(item, evt) {
      try {
        const label = `${this.displayText(item)} | ${
          item?.url || ''
        }`.trim();
        // Si estamos en  entorno local de pruebas, no se registra el evento
        if (!IS_LOCAL_TEST) {
          window.gtag?.('event', 'Click', {
            event_category: 'Menu',
            event_label: label,
          });
        }
      } catch (e) {
        this.notifyError(
          e,
          'No se pudo registrar el evento de analítica'
        );
      }

      if (!item || !item.url) {
        if (evt && typeof evt.preventDefault === 'function') {
          evt.preventDefault();
        }
        return;
      }

      // Marcamos activo de forma optimista para dar sensación de respuesta inmediata
      this.curPath = this.normalizePath(item.url);

      // Si se prefiere navegar con código, se puede usar navigate en lugar de href
    },

    onHelpClick(evt) {
      if (evt && typeof evt.preventDefault === 'function') {
        evt.preventDefault();
      }

      try {
        // Si estamos en  entorno local de pruebas, no se registra el evento
        if (!IS_LOCAL_TEST) {
          window.gtag?.('event', 'Click', {
            event_category: 'Menu',
            event_label: 'HelpLink',
          });
        }
      } catch (e) {
        this.notifyError(
          e,
          'No se pudo registrar el evento de ayuda'
        );
      }

      // Selecciona el correo en función del idioma
      let mail = 'info@agqlabs.com';
      if (this.getLanguage === 'es') {
        mail = 'atencioncliente.esp@agqlabs.com';
      } else if (this.getLanguage === 'en') {
        mail = 'info@agqlabs.com';
      } else if (this.getLanguage === 'it') {
        mail = 'italia@agqlabs.com';
      } else if (this.getLanguage === 'pt') {
        mail = 'infoportugal@agqlabs.com';
      }

      window.open(`mailto:${mail}`, '_blank');
    },

    goBack() {
      const detailedSample =
        this.$store?.state?.samplesRegisterModule?.detailedSample;
      const templateCreationDraft =
        this.$store?.state?.samplingTemplatesModule
          ?.templateCreationDraft;
      const isTemplateCreateFromSimpleSamplesRegister =
        this.currentPath ===
          '/TemplatesManager/TemplatesList/Create' &&
        templateCreationDraft?.sourceRouteName ===
          'SamplesRegister' &&
        templateCreationDraft?.sourceRegisterMode === 'express';

      if (
        this.currentPath === '/SamplesRegister/SamplesDetails' &&
        detailedSample?.navigationSource ===
          'samplesRegisterConfirmation'
      ) {
        const router =
          this.$f7?.views?.main?.router || this.$f7router;

        router?.navigate(
          {
            name: 'SamplesRegister',
            query: { tab: '1' },
          },
          {
            animate: true,
            transition: 'f7-fade',
            replaceState: true,
          }
        );
        return;
      }

      if (isTemplateCreateFromSimpleSamplesRegister) {
        const router =
          this.$f7?.views?.main?.router || this.$f7router;

        router?.navigate(
          {
            name: 'SamplesRegister',
            query: {
              mode: 'express',
              tab: String(
                Number.isInteger(
                  templateCreationDraft?.sourceRegisterTab
                )
                  ? templateCreationDraft.sourceRegisterTab
                  : 0
              ),
            },
          },
          {
            animate: true,
            transition: 'f7-fade',
            replaceState: true,
          }
        );
        return;
      }

      this.goBackSmart();
    },

    // goBack() {
    //   try {
    //     const view = this.$f7?.views?.main;
    //     const router = view?.router || this.$f7router;

    //     const cur = router?.currentRoute?.path || '/';
    //     const prev = router?.previousRoute?.path || '/';

    //     // Si no hay ruta previa o es igual a la actual, ir al home
    //     if (!prev || prev === cur) {
    //       router.navigate('/', {
    //         animate: true,
    //         transition: 'f7-fade',
    //       });
    //       return;
    //     }

    //     // Detectar patrón de ping-pong: A, B, A, B
    //     if (
    //       this.lastBackRoute === prev &&
    //       this.lastPrevRoute === cur
    //     ) {
    //       this.lastBackRoute = null;
    //       this.lastPrevRoute = null;
    //       router.navigate('/', {
    //         animate: true,
    //         transition: 'f7-fade',
    //       });
    //       return;
    //     }

    //     // Guardar para próxima detección
    //     this.lastPrevRoute = prev;
    //     this.lastBackRoute = cur;

    //     router.navigate(prev, {
    //       animate: true,
    //       transition: 'f7-fade',
    //     });
    //   } catch (e) {
    //     this.notifyError(e, 'No se pudo navegar hacia atrás');
    //   }
    // },

    // Manejo uniforme de errores, siguiendo el patrón de notifyDX usado en la app
    notifyError(e, fallbackMessage = 'Ha ocurrido un error') {
      try {
        this.notificationMessage = '';
        let msg = fallbackMessage;

        try {
          const parsed = JSON.parse(e?.message || '{}');
          const filtered = this.$helpers?.getFilteredErrorMessage?.(
            parsed?.error
          );
          if (filtered) msg = this.$t ? this.$t(filtered) : filtered;
        } catch {
          if (e && e.message) msg = e.message;
        }

        this.$notifyDX(
          {
            message: msg,
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      } finally {
        setTimeout(() => {
          this.notificationMessage = '';
        }, 2000);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './Menu.styles.scss';
</style>
